/**
 * Componente de links de redes sociais
 */

import type { EstablishmentPublic } from "../types";
import { buildEstablishmentWhatsappHref } from "../../../lib/whatsappLink";
import { generateGoogleMapsLink, generateWazeLink, normalizeSocial } from "../utils";
import { FiMapPin } from "react-icons/fi";
import {
  SiGooglemaps,
  SiInstagram,
  SiTiktok,
  SiWaze,
  SiYoutube,
  SiWhatsapp,
} from "react-icons/si";

interface SocialLinksProps {
  establishment: EstablishmentPublic;
  textColor?: string;
}

export function SocialLinks({ establishment, textColor }: SocialLinksProps) {
  // CAD-29: valor que não é telefone nem URL do WhatsApp não vira link.
  const whatsappHref = buildEstablishmentWhatsappHref(establishment.socialWhatsapp);
  const hasSocial =
    establishment.socialInstagram ||
    establishment.socialTiktok ||
    establishment.socialYoutube ||
    whatsappHref;
  const googleMapsUrl = generateGoogleMapsLink(
    establishment.mapLat,
    establishment.mapLng
  );
  const wazeUrl = generateWazeLink(establishment.mapLat, establishment.mapLng);
  const hasMapLinks = Boolean(googleMapsUrl || wazeUrl);

  const color = textColor || "#FFFFFF";

  if (!hasSocial && !hasMapLinks) return null;

  const iconClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-white/10 text-sm transition hover:bg-white/20";

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" style={{ color }}>
      {establishment.socialInstagram && (
        <a
          href={normalizeSocial(establishment.socialInstagram, "instagram")}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className={iconClass}
          style={{ color }}
        >
          <SiInstagram aria-hidden />
        </a>
      )}

      {establishment.socialTiktok && (
        <a
          href={normalizeSocial(establishment.socialTiktok, "tiktok")}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="TikTok"
          className={iconClass}
          style={{ color }}
        >
          <SiTiktok aria-hidden />
        </a>
      )}

      {establishment.socialYoutube && (
        <a
          href={normalizeSocial(establishment.socialYoutube, "youtube")}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
          className={iconClass}
          style={{ color }}
        >
          <SiYoutube aria-hidden />
        </a>
      )}

      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className={iconClass}
          style={{ color }}
        >
          <SiWhatsapp aria-hidden />
        </a>
      )}

      {hasMapLinks && (
        <details className="group relative" onClick={(event) => event.stopPropagation()}>
          <summary
            aria-label="Abrir localização"
            className={`${iconClass} cursor-pointer list-none focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 [&::-webkit-details-marker]:hidden`}
            style={{ color }}
          >
            <FiMapPin aria-hidden />
          </summary>

          <div className="absolute bottom-full left-1/2 z-50 mb-2 min-w-[190px] -translate-x-1/2 overflow-hidden rounded-2xl border border-white/20 bg-white p-1.5 text-slate-900 shadow-xl shadow-black/20">
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition hover:bg-slate-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-100"
              >
                <SiGooglemaps aria-hidden className="text-[#4285F4]" />
                <span>Google Maps</span>
              </a>
            )}

            {wazeUrl && (
              <a
                href={wazeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition hover:bg-slate-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-100"
              >
                <SiWaze aria-hidden className="text-[#33CCFF]" />
                <span>Waze</span>
              </a>
            )}
          </div>
        </details>
      )}
    </div>
  );
}
